/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Icon } from '@zapier/zinnia-icons';
import { Text } from '@zapier/design-system';
import Link from 'app/common/components/Link';
import { PartnerInteractionType } from '../utils';
import { ExhaustiveError } from './ExhaustiveError';
import { CustomStatus } from './Status';
import withTrackInteraction, {
  WithPartnerUserInteractionTrackerProps,
} from 'app/developer-v3/teams/team-partner-sharing/withPartnerUserInteractionTracker';
import { PARTNER_SOLUTIONS_BASE_DOC_URL } from 'app/developer-v3/constants';

const styleRoot = css`
  padding: 70px 0;
  text-align: center;
  max-width: 620px;
  /* Google Chart default height */
  min-height: 375px;
`;

const styleInlineBlock = css`
  display: inline-block;
`;

interface EmbedInsightsTileEmptyProps
  extends WithPartnerUserInteractionTrackerProps {
  appId: string;
  interactionType: PartnerInteractionType;
}

export function BaseEmbedInsightsTileEmpty({
  appId,
  handleTrackInteraction,
  interactionType,
}: EmbedInsightsTileEmptyProps) {
  return (
    <div css={styleRoot}>
      <CustomStatus>
        <Text tag="p" type="Body3">
          No results
        </Text>
        {(() => {
          switch (interactionType) {
            case 'all':
              return (
                <Text tag="p" type="Body1">
                  You have not embedded Zapier or your embeds have no click
                  data. Bring thousands of integrations into your product by{' '}
                  <Link
                    href={`/app/${appId}/embed`}
                    onClick={() => {
                      handleTrackInteraction({
                        eventAction: 'click',
                        interactionGoal:
                          'Views embed zapier details from Embed Insights',
                        interactionName: 'embed_insights_cta_ALL',
                      });
                    }}
                  >
                    {/* Avoid line wrapping mid-link */}
                    <span css={styleInlineBlock}>embedding Zapier</span>
                  </Link>
                  .
                </Text>
              );
            case 'workflow_element':
              return (
                <Text tag="p" type="Body1">
                  You have not embedded the Workflow Element or it has no click
                  data. Users can discover, create, and edit Zaps inside your
                  app with the{' '}
                  <Link
                    href="https://zapier.com/partner/embed/workflow"
                    onClick={() => {
                      handleTrackInteraction({
                        eventAction: 'click',
                        interactionGoal:
                          'Views embed zapier details from Embed Insights',
                        interactionName: 'embed_insights_cta_FZE',
                      });
                    }}
                    target="_blank"
                  >
                    {/* Avoid line wrapping mid-link */}
                    <span css={styleInlineBlock}>
                      Workflow Element <Icon name="arrowOffsite" size={16} />
                    </span>
                  </Link>
                </Text>
              );
            case 'workflow_api':
              return (
                <Text tag="p" type="Body1">
                  You have not embedded using Workflow API or it has no click
                  data. The{' '}
                  <Link
                    href={`${PARTNER_SOLUTIONS_BASE_DOC_URL}/workflow-api/intro`}
                    onClick={() => {
                      handleTrackInteraction({
                        eventAction: 'click',
                        interactionGoal:
                          'Views embed zapier details from Embed Insights',
                        interactionName: 'embed_insights_cta_PAPI',
                      });
                    }}
                    target="_blank"
                  >
                    {/* Avoid line wrapping mid-link */}
                    <span css={styleInlineBlock}>
                      Workflow API <Icon name="arrowOffsite" size={16} />
                    </span>
                  </Link>{' '}
                  gives you full UI control over your user's Zapier experience
                  in your app.
                </Text>
              );

            default:
              throw new ExhaustiveError(interactionType);
          }
        })()}
      </CustomStatus>
    </div>
  );
}

export const EmbedInsightsTileEmpty = withTrackInteraction(
  BaseEmbedInsightsTileEmpty
);
